import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Group } from '@/types/group';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

interface PublishConfig {
  groups: string[];
  deadline: Date | null;
  visibleAfterDeadline: boolean;
}

interface PublishModalProps {
  onClose: () => void;
  onConfirm: (config: PublishConfig) => void;
}

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function buildCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells: { day: number; date: Date; current: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: prevMonthDays - i, date: new Date(year, month - 1, prevMonthDays - i), current: false });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, date: new Date(year, month, d), current: true });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ day: d, date: new Date(year, month + 1, d), current: false });
  return cells;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function PublishModal({ onClose, onConfirm }: PublishModalProps) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [visibleAfterDeadline, setVisibleAfterDeadline] = useState(false);
  const groupsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<Group[]>('/groups').then(setGroups).catch(() => {});
  }, []);

  useEffect(() => {
    if (!groupsOpen) return;
    function handler(e: MouseEvent) {
      if (groupsRef.current && !groupsRef.current.contains(e.target as Node))
        setGroupsOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [groupsOpen]);

  function toggleGroup(id: string) {
    setSelectedGroups((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  }

  const selectedNames = groups
    .filter((g) => selectedGroups.includes(g.id))
    .map((g) => g.name)
    .join(', ');

  const deadlineLabel = deadline
    ? deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : t('modals.publish.pickDate');

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth =
    viewDate.getFullYear() === today.getFullYear() && viewDate.getMonth() === today.getMonth();

  const cells = buildCells(viewDate.getFullYear(), viewDate.getMonth());
  const monthLabel = `${MONTH_NAMES[viewDate.getMonth()]} ${viewDate.getFullYear()}`;

  return (
    <ModalShell title={t('modals.publish.title')} onClose={onClose} maxWidth='max-w-sm'>
      <div className='flex flex-col gap-6'>

        {/* Groups */}
        <div className='flex items-start gap-4'>
          <span className='w-20 shrink-0 pt-2.5 text-sm font-medium text-ws-text'>{t('modals.publish.groups')}</span>
          <div className='relative flex-1' ref={groupsRef}>
            <button
              onClick={() => setGroupsOpen((o) => !o)}
              className='flex w-full items-center justify-between rounded-lg border border-ws-border bg-ws-btn px-3 py-2.5 text-sm hover:bg-ws-hover'
            >
              <span className={`truncate ${selectedGroups.length === 0 ? 'text-ws-muted' : 'text-ws-text'}`}>
                {selectedGroups.length === 0 ? t('modals.publish.selectGroups') : selectedNames}
              </span>
              <ChevronDown className='ml-2 h-4 w-4 shrink-0 text-ws-muted' />
            </button>
            {groupsOpen && (
              <div className='absolute left-0 right-0 top-11 z-10 max-h-52 overflow-y-auto rounded-lg border border-ws-border bg-ws-elevated shadow-lg'>
                {groups.length === 0 && (
                  <p className='px-3 py-3 text-sm text-ws-muted'>{t('modals.publish.noGroups')}</p>
                )}
                {groups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    className='flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-ws-text hover:bg-ws-hover'
                  >
                    <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${selectedGroups.includes(g.id) ? 'border-ws-primary bg-ws-primary' : 'border-ws-border'}`}>
                      {selectedGroups.includes(g.id) && (
                        <svg className='h-2.5 w-2.5 text-white' viewBox='0 0 10 8' fill='none'>
                          <path d='M1 4l3 3 5-6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                      )}
                    </div>
                    <span className='truncate'>{g.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Deadline */}
        <div className='flex items-start gap-4'>
          <span className='w-20 shrink-0 pt-2.5 text-sm font-medium text-ws-text'>{t('modals.publish.deadline')}</span>
          <div className='flex-1'>
            <button
              onClick={() => setCalendarOpen((o) => !o)}
              className='flex w-full items-center gap-2 rounded-lg border border-ws-border bg-ws-btn px-3 py-2.5 text-sm hover:bg-ws-hover'
            >
              <CalendarDays className='h-4 w-4 shrink-0 text-ws-muted' />
              <span className={deadline ? 'text-ws-text' : 'text-ws-muted'}>{deadlineLabel}</span>
            </button>
            <div className='flex justify-end'>
              <span className='text-xs text-ws-muted'>{t('common.optional')}</span>
            </div>

            {calendarOpen && (
              <div className='mt-2 rounded-xl border border-ws-border bg-ws-elevated p-3'>
                <div className='mb-3 flex items-center justify-between'>
                  <button
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1))}
                    disabled={isCurrentMonth}
                    className='flex h-7 w-7 border border-ws-border rounded bg-ws-btn items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-ws-btn'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  <span className='text-sm font-semibold text-ws-text'>{monthLabel}</span>
                  <button
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1))}
                    className='flex h-7 w-7 border border-ws-border rounded bg-ws-btn items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
                <div className='mb-1 grid grid-cols-7 text-center'>
                  {DAY_HEADERS.map((d) => (
                    <span key={d} className='text-xs text-ws-muted'>{d}</span>
                  ))}
                </div>
                <div className='grid grid-cols-7'>
                  {cells.map((cell, i) => {
                    const selected = deadline && sameDay(cell.date, deadline);
                    const isPast = cell.date < today;
                    return (
                      <button
                        key={i}
                        disabled={isPast}
                        onClick={() => { setDeadline(cell.date); setCalendarOpen(false); }}
                        className={`flex h-8 w-full items-center justify-center rounded-full text-sm transition-colors
                          ${selected ? 'bg-ws-primary font-semibold text-white' : ''}
                          ${!selected && !isPast && cell.current ? 'text-ws-text hover:bg-ws-hover' : ''}
                          ${!selected && (isPast || !cell.current) ? 'text-ws-text-disabled cursor-not-allowed' : ''}
                        `}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        
        </div>
        

        {/* Visible after deadline */}
        <label className={`flex cursor-pointer items-center gap-3 ${!deadline ? 'cursor-default opacity-50' : ''}`}>
          <input
            type='checkbox'
            disabled={!deadline}
            checked={visibleAfterDeadline}
            onChange={(e) => setVisibleAfterDeadline(e.target.checked)}
            className='h-4 w-4 accent-ws-primary'
          />
          <span className='text-sm text-ws-text'>{t('modals.publish.visibleAfterDeadline')}</span>
        </label>

        {/* Confirm */}
        <div className='flex flex-1 justify-end'>
          <Button variant='primary' size='lg'
            onClick={() => onConfirm({ groups: selectedGroups, deadline, visibleAfterDeadline })}>
            {t('modals.publish.confirm')}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
